import { useEffect, useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Checkbox } from '@any-ui-react/core';

import { AnyChatMediaUpload, AnyChatForm, CrmRichTextEditor } from '@anyx/feature-crm/shared';

import { useBroadcastImageUpload } from '../../hooks';
import { useLineMessageStore } from '../../store';
import { LineMessageFormFieldsProps, ButtonTypeBlockFieldErrors } from '../../types';

import { ButtonTypeActionButtons } from './ButtonTypeActionButtons';

export function ButtonTypeBlockCard({
  blockIndex,
  cardIndex,
}: {
  blockIndex: number;
  cardIndex: number;
}) {
  const { t } = useTranslation('crm');
  const isFormDisabled = useLineMessageStore((state) => state.isFormDisabled);
  const variablePluginOptions = useLineMessageStore((store) => store.variablePluginOptions);

  const { register, control, unregister, setValue, formState } =
    useFormContext<LineMessageFormFieldsProps>();

  const imageFiles = useWatch({
    control,
    name: `messages.${blockIndex}.cards.${cardIndex}.image` as const,
  });

  const mediaUploadProps = useBroadcastImageUpload({
    messageType: 'BUTTON',
    onImageUpdate: (image) => {
      setValue(`messages.${blockIndex}.cards.${cardIndex}.image` as const, image, {
        shouldValidate: formState.submitCount > 0,
        shouldDirty: true,
        shouldTouch: true,
      });
    },
  });

  const error = useMemo(() => {
    const errorMsg = formState.errors?.messages?.[blockIndex] as ButtonTypeBlockFieldErrors;

    if (errorMsg && 'cards' in errorMsg) {
      return errorMsg.cards?.[cardIndex];
    }

    return undefined;
  }, [blockIndex, formState, cardIndex]);

  useEffect(() => {
    register(`messages.${blockIndex}.cards.${cardIndex}.image`);

    return () => {
      unregister(
        [
          `messages.${blockIndex}.cards.${cardIndex}.image` as const,
          `messages.${blockIndex}.cards.${cardIndex}.title` as const,
          `messages.${blockIndex}.cards.${cardIndex}.description` as const,
        ],
        {
          keepDefaultValue: true,
          keepDirty: true,
          keepError: true,
          keepIsValid: true,
          keepTouched: true,
          keepValue: true,
        }
      );
    };
  }, [register, unregister, blockIndex, cardIndex]);

  return (
    <AnyChatForm.Block className="grow">
      <AnyChatMediaUpload
        {...mediaUploadProps}
        inputWrapperProps={{
          label: t('crm.lineMessageForm.fields.image.label'),
          error: error?.image?.message,
        }}
        files={imageFiles ? [imageFiles] : []}
        disabled={isFormDisabled}
      />

      <Checkbox
        {...register(`messages.${blockIndex}.cards.${cardIndex}.isImageBelowDescription` as const)}
        label={t('crm.lineMessageForm.fields.image.belowDescription')}
        disabled={isFormDisabled}
      />

      <Controller
        control={control}
        name={`messages.${blockIndex}.cards.${cardIndex}.title` as const}
        render={({ field: { value, onChange } }) => (
          <CrmRichTextEditor
            withInlineState
            withTrackingLink
            editable={!isFormDisabled}
            variablePluginOptions={variablePluginOptions}
            size="xs"
            wrapper={{
              label: t('crm.lineMessageForm.fields.title.label'),
              error: error?.title?.message,
            }}
            onChange={({ richText, align, fontSize, weighted, text }) =>
              onChange({ ...value, richText, align, fontSize, weighted, text })
            }
            initialInlineState={{
              weighted: value.weighted,
              fontSize: value.fontSize,
              align: value.align,
            }}
            initialEditorState={value.richText}
          />
        )}
      />

      <Controller
        control={control}
        name={`messages.${blockIndex}.cards.${cardIndex}.description` as const}
        render={({ field: { value, onChange } }) => (
          <CrmRichTextEditor
            withInlineState
            withTrackingLink
            editable={!isFormDisabled}
            variablePluginOptions={variablePluginOptions}
            size="sm"
            wrapper={{
              label: t('crm.lineMessageForm.fields.description.label'),
              error: error?.description?.message,
            }}
            onChange={({ richText, align, fontSize, weighted, text }) =>
              onChange({ ...value, richText, align, fontSize, weighted, text })
            }
            initialInlineState={{
              weighted: value.weighted,
              fontSize: value.fontSize,
              align: value.align,
            }}
            initialEditorState={value.richText}
          />
        )}
      />

      <ButtonTypeActionButtons blockIndex={blockIndex} cardIndex={cardIndex} />
    </AnyChatForm.Block>
  );
}
